import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import useObjectState from 'src/hooks/useObjectState';
import { useCodeSystem, useOrganizations } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { GridItem, GridSection } from 'src/components/CustomModal';
import { getFullAddress } from 'src/careflow/common/common-utils';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  ReferralService,
  ServiceByFiltersResponse,
} from 'src/crs/referral/services/ReferralService';
import { Address } from 'src/careflow/ph-core/fhir-client/resources';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import {
  OrganizationWrapper,
  WrappedOrganization,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { capitalize } from 'src/utils/string';
import { internalExternalOrg } from './constants';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { fetchOrganizationAffiliation } from 'src/careflow/helpers/common';
import { getReferenceId } from 'src/utils/fhir';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import uuidv4 from 'src/utils/uuidv4';

type Props = {
  patient?: WrappedPatient | null;
  updateState: any;
  openSearch: boolean;
};

const SearchServices = ({ patient, updateState, openSearch }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLocales();
  const authUser = useAuth().getCurrentUser();
  const [state, updateStateFilters] = useObjectState<any>({});
  const [services, setServices] = useState<any>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<WrappedOrganization | null>(
    null
  );
  const [organizationInternalExternal, setOrganizationInternalExternal] = useState<string[]>([]);
  const [disabledFilterByCoverageArea, setDisabledFilterByCoverageArea] = useState(false);
  const [coverageByArea, setCoverageByArea] = useState<any>([]);
  const [serviceName, setServiceName] = useState('');
  const [internalExternal, setInternalExternal] = useState<string[]>(['all']);

  const [serviceType] = useCodeSystem('ph-referral-services');
  const [organizations, { isFetching: isOrganizationsLoading }] = useOrganizations({
    filter: {
      active: true,
    },
    map: OrganizationWrapper,
  });

  const userOrganizations = useMemo(() => authUser?.organizations ?? [], [authUser?.organizations]);

  const orgsKey = useMemo(() => JSON.stringify(userOrganizations), [userOrganizations]);

  const managingOrgRef = patient?.managingOrganization?.reference;

  useEffect(() => {
    const getOrgsInternalExternal = async () => {
      if (!internalExternal?.length || internalExternal.includes('all')) {
        setOrganizationInternalExternal([]);
        return;
      }

      const checkedInternal = internalExternal.includes('internal');
      const checkedExternal = internalExternal.includes('external');

      let result: string[] = [];

      const patientManagingOrgId = getReferenceId(managingOrgRef);
      const baseOrganizations = patientManagingOrgId ? [patientManagingOrgId] : [orgsKey];

      if (checkedExternal) {
        const affiliations = await fetchOrganizationAffiliation(baseOrganizations);
        result = affiliations;
      }

      if (checkedInternal) {
        result = [...result, ...(baseOrganizations ?? [])];
      }

      setOrganizationInternalExternal(Array.from(new Set(result)));
    };

    getOrgsInternalExternal();
  }, [internalExternal, managingOrgRef, orgsKey]);

  const { serviceTypeItem, postalCode, city, states, serviceCoverageArea, isLoading } = state;

  const referralServiceTypes = useMemo(
    () =>
      serviceType?.concept
        ?.map((type) => ({
          label: type.display,
          value: type.code,
        }))
        .sort((x1, x2) =>
          x1.label && x2.label && x1.label > x2.label
            ? 1
            : x1.label && x2.label && x1.label < x2.label
            ? -1
            : 0
        ) ?? [],
    [serviceType]
  );

  const postalCodes = useMemo(
    () =>
      patient?.address
        ?.map((address: Address) => ({
          label: address?.postalCode,
          value: address?.postalCode,
        }))
        .sort((x1, x2) =>
          x1.label && x2.label && x1.label > x2.label
            ? 1
            : x1.label && x2.label && x1.label < x2.label
            ? -1
            : 0
        ) ?? [],
    [patient]
  );

  const statesPatient = useMemo(
    () =>
      patient?.address
        ?.map((address: Address) => ({
          label: address?.state,
          value: address?.state,
        }))
        .sort((x1, x2) =>
          x1.label && x2.label && x1.label > x2.label
            ? 1
            : x1.label && x2.label && x1.label < x2.label
            ? -1
            : 0
        ) ?? [],
    [patient]
  );

  const citiesPatient = useMemo(
    () =>
      patient?.address
        ?.map((address: Address) => ({
          label: address?.city,
          value: address?.city,
        }))
        .sort((x1, x2) =>
          x1.label && x2.label && x1.label > x2.label
            ? 1
            : x1.label && x2.label && x1.label < x2.label
            ? -1
            : 0
        ) ?? [],
    [patient]
  );

  useEffect(() => {
    if (!openSearch) return;

    const getCoverageArea = async () => {
      if (orgsKey?.length > 0) {
        const locations = await ReferralService.getLocationFromOrganizationUsers(userOrganizations);
        const locationIds = locations?.map((e) => e?.id);

        if (locationIds && locationIds?.length > 0) {
          setCoverageByArea(locationIds);
        } else {
          setDisabledFilterByCoverageArea(true);
        }
      }
    };
    getCoverageArea();
  }, [openSearch, serviceCoverageArea, orgsKey]);

  const handleSearchServices = async () => {
    setServices([]);

    const organizationIds = selectedOrganization
      ? selectedOrganization?.id
      : organizationInternalExternal?.length > 0
      ? organizationInternalExternal
      : null;

    const searchParams = {
      serviceType: serviceTypeItem?.value || serviceTypeItem,
      postalCode: postalCode?.value || postalCode,
      state: states?.value || states,
      organizationId: organizationIds,
      serviceName,
      city: city?.value || city,
      ...(serviceCoverageArea && { serviceCoverageArea: coverageByArea }),
    };

    updateStateFilters({ isLoading: true });

    try {
      const servicesResponse = await ReferralService.getServicesByFilters(searchParams);
      if (servicesResponse) {
        setServices(servicesResponse);
      }
    } catch (error) {
      enqueueSnackbar(`An error has occurred.`, { variant: 'error' });
    }
    updateStateFilters({ isLoading: false });
  };

  const handleSelectedService = (data: ServiceByFiltersResponse) => {
    updateState({
      serviceTypeItem: {
        label: `${data?.healthcareService?.name}${
          data?.organization?.name ? ` | ${data?.organization?.name}` : ''
        } `,
        value: {
          healthcareService: data?.healthcareService,
          organization: data?.organization,
        },
      },
      locationOrganization: {
        label: data?.location?.name,
        value: {
          location: data?.location,
        },
      },
      openSearch: false,
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'healthcareService',
      headerName: 'Service',
      flex: 1,
      renderCell: (params) => setRow(params?.row?.healthcareService),
    },
    {
      field: 'organization',
      headerName: 'Organization',
      flex: 1,
      renderCell: (params) => setRow(params?.row?.organization),
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      renderCell: (params) => setRow(params?.row?.location),
    },
    {
      field: 'edit',
      headerName: '',
      flex: 0.3,
      sortable: false,
      renderCell: (params) => {
        const { healthcareService } = params.row;
        return !isEmpty(healthcareService) ? (
          <Button
            sx={{
              height: 36,
              width: 63,
            }}
            onClick={() => {
              handleSelectedService(params.row);
            }}
            variant="contained"
          >
            Select
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <>
      <Grid item xs={12} mt={0}>
        <form>
          <GridSection>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.serviceName', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  value={serviceName}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const text = event.target.value;
                    setServiceName(text);
                  }}
                  variant="standard"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.serviceType', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={referralServiceTypes}
                  value={serviceTypeItem}
                  onChange={(_: any, value: any | null) => {
                    if (value && value?.value) {
                      updateStateFilters({ serviceTypeItem: value });
                    }
                  }}
                  onInputChange={(_: any, value: any | null) => {
                    updateStateFilters({ serviceTypeItem: value });
                  }}
                  renderInput={(params) => <TextField required variant="standard" {...params} />}
                />
              </FormControl>
            </GridItem>
          </GridSection>
          <GridSection>
            <GridItem>
              <FormControl fullWidth>
                <FormGroup row>
                  {internalExternalOrg.map(({ code, display }) => (
                    <FormControlLabel
                      key={code}
                      control={
                        <Checkbox
                          checked={internalExternal?.includes(code || '')}
                          onChange={(e) => {
                            const { checked } = e.target;
                            setInternalExternal((prev) => {
                              const newCode = code || '';
                              if (checked) {
                                if (newCode === 'all') {
                                  return ['all'];
                                } else {
                                  const filtered = prev.filter((c) => c !== 'all');
                                  return [...filtered, newCode];
                                }
                              } else {
                                return prev.filter((c) => c !== newCode);
                              }
                            });
                          }}
                        />
                      }
                      label={i18n(`patients.details.serviceRequests.searchService.${code}`, 'crs')}
                      sx={{ py: 1, flexGrow: 1 }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </GridItem>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.organization', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  value={selectedOrganization}
                  fullWidth
                  onChange={(_, organization) => {
                    setSelectedOrganization(organization);
                  }}
                  options={
                    !internalExternal.includes('all') &&
                    (!organizationInternalExternal || organizationInternalExternal.length === 0)
                      ? []
                      : internalExternal.includes('all')
                      ? organizations ?? []
                      : organizations?.filter((e) =>
                          organizationInternalExternal?.some((s) => s === e?.id)
                        ) ?? []
                  }
                  getOptionLabel={(organization: WrappedOrganization) =>
                    capitalize(organization?.name ?? '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isOrganizationsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </FormControl>
            </GridItem>
          </GridSection>
          <GridSection>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.postalCode', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={postalCodes}
                  value={postalCode}
                  onChange={(_: any, value: any | null) => {
                    updateStateFilters({ postalCode: value });
                  }}
                  onInputChange={(_: any, value: any | null) => {
                    updateStateFilters({ postalCode: value });
                  }}
                  renderInput={(params) => <TextField required variant="standard" {...params} />}
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.city', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={citiesPatient}
                  value={city}
                  onChange={(_: any, value: any | null) => {
                    updateStateFilters({ city: value });
                  }}
                  onInputChange={(_: any, value: any | null) => {
                    updateStateFilters({ city: value });
                  }}
                  renderInput={(params) => <TextField required variant="standard" {...params} />}
                />
              </FormControl>
            </GridItem>
          </GridSection>
          <GridSection>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.state', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={statesPatient}
                  value={states}
                  onChange={(_: any, value: any | null) => {
                    updateStateFilters({ states: value });
                  }}
                  onInputChange={(_: any, value: any | null) => {
                    updateStateFilters({ states: value });
                  }}
                  renderInput={(params) => <TextField required variant="standard" {...params} />}
                />
              </FormControl>
            </GridItem>
            <GridItem sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'stretch' }}>
              <Tooltip
                title={
                  disabledFilterByCoverageArea
                    ? i18n(
                        'patients.details.serviceRequests.searchService.notFoundCoverageByArea',
                        'crs'
                      )
                    : i18n(
                        'patients.details.serviceRequests.searchService.filterCoverageByArea',
                        'crs'
                      )
                }
                children={
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={isLoading || disabledFilterByCoverageArea}
                        checked={!!serviceCoverageArea}
                        onClick={() =>
                          updateStateFilters({ serviceCoverageArea: !serviceCoverageArea })
                        }
                      />
                    }
                    label={i18n(
                      'patients.details.serviceRequests.searchService.filterCoverageByArea',
                      'crs'
                    )}
                  />
                }
              />
            </GridItem>
          </GridSection>
          <Box mt={2} display={'flex'} justifyContent={'flex-end'}>
            <Button variant="contained" onClick={handleSearchServices}>
              {i18n('patients.details.serviceRequests.searchService.search', 'crs')}
            </Button>
          </Box>
        </form>
      </Grid>
      <GridSection>
        <GridItem xs={12} sx={{ ml: 1 }}>
          <Box sx={{ width: '100%', height: 350 }}>
            <DataGrid
              rows={services ?? []}
              sx={{
                width: '100% !important',
                '& .MuiDataGrid-columnHeader': { backgroundColor: '#f4f6f8' },
                '& .MuiDataGrid-cell': {
                  border: 0,
                },
              }}
              loading={isLoading}
              getRowId={({ healthcareService }) => healthcareService?.id ?? uuidv4()}
              getRowSpacing={() => ({ bottom: 5 })}
              getRowHeight={() => 'auto'}
              getEstimatedRowHeight={() => 90}
              columns={columns}
              components={{
                Footer: () => <></>,
              }}
              rowSpacingType="margin"
              isRowSelectable={() => false}
              disableColumnFilter
              disableColumnMenu
              density={'compact'}
            />
          </Box>
        </GridItem>
      </GridSection>
    </>
  );
};

const setRow = (row: any) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box>
      <Typography variant="subtitle1">{row?.name}</Typography>
    </Box>

    <Box>{row?.comment}</Box>

    {row?.address ? <Box>{getFullAddress(row?.address)}</Box> : null}

    {row?.telecom &&
      row?.telecom?.map((t: any, index: number) => (
        <Box key={'box-telecom-' + index}>{t?.value}</Box>
      ))}
  </Box>
);

export default SearchServices;
